import { Injectable } from '@angular/core';
import MapLayerType from '@app/models-internal/enums/map-layer-type.enum';
import { BaseLayerOptions } from 'ol-layerswitcher';
import { getCenter } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import { Image as ImageLayer } from 'ol/layer';
import OlTileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OlMap from 'ol/Map';
import { ImageArcGISRest } from 'ol/source';
import OSM from 'ol/source/OSM';
import TileImage from 'ol/source/TileImage';
import VectorSource from 'ol/source/Vector';
import OlXYZ from 'ol/source/XYZ';
import { transformExtent } from 'ol/proj';
import { LogService, NotificationService } from '@oa/shared-core';
import { OpenLayerStyleService } from './open-layer-style.service';
import { SpatialService } from './spatial.service';

declare var REP: any;
/**
 * Service for general Openlayer Methods
 * https://openlayers.org/en/latest/apidoc/
 */
@Injectable()
export class OpenLayerMapService {
  private readonly _componentName = 'OpenLayerMapService';

  constructor(
    private log: LogService,
    private notify: NotificationService,
    private styleService: OpenLayerStyleService,
    private spatialService: SpatialService
  ) {}

  public createNewLayer(type: MapLayerType) {
    switch (type) {
      case MapLayerType.OSM:
        return new OlTileLayer({
          name: 'OpenStreetMap',
          title: 'OSM',
          type: 'base',
          source: new OSM({
            url: 'https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.EsriWorldImagery:
        return new OlTileLayer({
          name: 'Esri World_Imagery',
          title: 'Aerials',
          type: 'base',
          source: new OlXYZ({
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            maxZoom: 19,
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.USDANAIP:
        return new OlTileLayer({
          name: 'USDA NAIP',
          title: 'USDA NAIP Imagery',
          type: 'base',
          source: new OlXYZ({
            url: 'https://gis.apfo.usda.gov/arcgis/rest/services/NAIP/USDA_CONUS_PRIME/ImageServer/tile/{z}/{y}/{x}',
            maxZoom: 19,
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.EsriWorldHillshade:
        return new OlTileLayer({
          name: 'Esri World Hillshade',
          title: 'Esri World Hillshade',
          type: 'base',
          source: new OlXYZ({
            url: ' http://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}.png',
            maxZoom: 19,
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.EsriWorldTopoMap:
        return new OlTileLayer({
          name: 'Esri World_Topo_Map',
          title: 'Roads',
          type: 'base',
          source: new OlXYZ({
            attributions:
              'Tiles © <a href="https://services.arcgisonline.com/ArcGIS/' + 'rest/services/World_Topo_Map/MapServer">ArcGIS</a>',
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/' + 'World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      /**
       * A ReportAll Parcel Layer
       * view-source:https://reportallusa.com/solutions/overlay/openlayers3.html
       */
      case MapLayerType.ReportAllParcel:
        return new OlTileLayer({
          name: 'Parcels',
          title: 'Parcels',
          maxResolution: 9,
          visible: true,
          source: new OlXYZ({
            url: REP.Layer.OpenLayers3.parcelsPrefixedURL(REP.ClientKey),
            maxZoom: 19,
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.NationalMapNHDFlowLinesSmallScale:
        // var url = "https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Rivers_and_Streams/FeatureServer";
        // var layer = '0';
        // var esrijsonFormat = new EsriJSON();

        return new ImageLayer({
          name: 'NHD Flowlines',
          title: 'NHD Flowlines',
          visible: false,
          source: new ImageArcGISRest({
            ratio: 1,
            params: {},
            url: 'https://hydro.nationalmap.gov/arcgis/rest/services/nhd/MapServer',
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      //

      case MapLayerType.EsriUSAMajorCities:
        const serviceUrl = 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Major_Cities/FeatureServer';
        const layerId = 0;
        const geometryType = 'esriGeometryEnvelope';

        //      loader: function (extent, resolution, projection) {}

        var vectorSource = new VectorSource({
          url: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Major_Cities/FeatureServer/0/query?where=%28ST%3D%27VA%27+OR+ST%3D%27NC%27+OR+ST%3D%27SC%27%29+AND+CLASS%3D%27city%27&objectIds=&time=&geometry=&geometryType=esriGeometryEnvelope&inSR=&spatialRel=esriSpatialRelIntersects&resultType=none&distance=0.0&units=esriSRUnit_Meter&returnGeodetic=false&outFields=NAME%2CST&returnGeometry=true&featureEncoding=esriDefault&multipatchOption=xyFootprint&maxAllowableOffset=&geometryPrecision=&outSR=&datumTransformation=&applyVCSProjection=false&returnIdsOnly=false&returnUniqueIdsOnly=false&returnCountOnly=false&returnExtentOnly=false&returnQueryGeometry=false&returnDistinctValues=false&cacheHint=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&having=&resultOffset=&resultRecordCount=&returnZ=false&returnM=false&returnExceededLimitFeatures=true&quantizationParameters=&sqlFormat=none&f=pgeojson&token=',
          format: new GeoJSON(), 
          // @ts-ignore
          crossOrigin: 'Anonymous',
        });

        const star = this.styleService.createBasicPointStyle('star', 'black', 'red');

        return new VectorLayer({
          name: 'Major Cities',
          title: 'Major Cities',
          source: vectorSource,
          style: () => {
            return star;
          },
        } as BaseLayerOptions);

      case MapLayerType.USGSTopo:
        return new OlTileLayer({
          name: 'USGS Topo',
          title: 'USGS',
          type: 'base',
          source: new OlXYZ({
            url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
            maxZoom: 19,
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.VAMostRecentImagery:
        return new OlTileLayer({
          name: 'Virginia BMP Imagery',
          title: 'Virginia BMP Imagery (Latest)',
          type: 'base',
          source: new OlXYZ({
            url: 'https://gismaps.vdem.virginia.gov/arcgis/rest/services/MostRecentImagery/MostRecentImagery_WGS/MapServer/tile/{z}/{y}/{x}',
            maxZoom: 19,
          }),
        } as BaseLayerOptions);

      case MapLayerType.GoogleHybrid:
        return new OlTileLayer({
          name: 'Google Satellite & Roads',
          title: 'Google Satellite & Roads',
          type: 'base',
          source: new TileImage({ url: 'http://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}' }),
        } as BaseLayerOptions);

      case MapLayerType.HEREAerialStreetTile:
        let urlTemplate =
          'https://{1-4}.{base}.maps.cit.api.here.com' +
          '/{type}/2.1/maptile/newest/{scheme}/{z}/{x}/{y}/256/png' +
          '?app_id={app_id}&app_code={app_code}';

        // https://developer.here.com/documentation/map-tile/dev_guide/topics/resource-aerial-streettile.html

        let layerDesc = {
          base: 'aerial',
          type: 'maptile',
          scheme: 'hybrid.day',
          app_id: 'eo2x8382v6oI5RWUQkWD',
          app_code: 'aS_JaLHkcC-YnvgYVCUOJA',
        };

        return new OlTileLayer({
          name: 'HERE Hybrid',
          title: 'HERE Hybrid',
          type: 'base',

          visible: false,
          preload: Infinity,
          source: new OlXYZ({
            url: this.createUrl(urlTemplate, layerDesc),
            attributions: 'Map Tiles &copy; ' + new Date().getFullYear() + ' ' + '<a href="http://developer.here.com">HERE</a>',
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      case MapLayerType.HERETerrainDay:
        let hereTerrainDayUrlTemplate =
          'https://{1-4}.{base}.maps.ls.hereapi.com' +
          '/{type}/2.1/basetile/newest/{scheme}/{z}/{x}/{y}/256/png' +
          '?app_id={app_id}&app_code={app_code}';

        // https://developer.here.com/documentation/map-tile/dev_guide/topics/resource-aerial-streettile.html

        let hereTerrainDayDesc = {
          base: 'aerial',
          type: 'maptile',
          scheme: 'terrain.day',
          app_id: 'eo2x8382v6oI5RWUQkWD',
          app_code: 'aS_JaLHkcC-YnvgYVCUOJA',
        };

        return new OlTileLayer({
          name: 'HERE Terrain',
          title: 'HERE Terrain',
          type: 'base',

          visible: false,
          preload: Infinity,
          source: new OlXYZ({
            url: this.createUrl(hereTerrainDayUrlTemplate, hereTerrainDayDesc),
            attributions: 'Map Tiles &copy; ' + new Date().getFullYear() + ' ' + '<a href="http://developer.here.com">HERE</a>',
            crossOrigin: 'Anonymous',
          }),
        } as BaseLayerOptions);

      default:
        break;
    }
  }

  private createUrl(tpl, layerDesc) {
    return tpl
      .replace('{base}', layerDesc.base)
      .replace('{type}', layerDesc.type)
      .replace('{scheme}', layerDesc.scheme)
      .replace('{app_id}', layerDesc.app_id)
      .replace('{app_code}', layerDesc.app_code);
  }

  public getMapCenter(map: OlMap) {
    if (!map) {
      this.notify.error('Map parameter is null.', null);
    }

    const g = map.getView().getCenter();

    return g;
  }

  public setCenterMapZoom(map: any, center: any, zoomLevel: number) {
    map.getView().setCenter(center);
    map.getView().setZoom(zoomLevel);
  }

  /**
   * Sets map to a zoom level if the map is above the max zoom
   * @param map
   * @param maxZoom
   */
  public setMaxZoom(map: any, maxZoom: number) {
    const currentZoom = this.getZoom(map);

    if (currentZoom > maxZoom) {
      map.getView().setZoom(maxZoom);
    }
  }

  /**
   * Gets the map's current zoom level
   * @param map
   */
  public getZoom(map: OlMap): number {
    return map.getView().getZoom();
  }

  /**
   * Gets the layer center point
   * @param layer
   */
  public getLayerCenter(layer: any) {
    const pt = getCenter(layer.getSource().getExtent());
    return pt;
  }

  public getLayerExtent(layer: any) {
    return layer.getSource().getExtent();
  }

  /**
   * Finds a map layer by name
   * @param map
   * @param layerName
   */
  public findLayerByName(map: any, layerName: string) {
    if (!map) {
      return null;
    }

    if (!layerName) {
      return null;
    }

    var layers = map.getLayers().getArray();

    for (let index = 0; index < layers.length; index++) {
      const layer = layers[index];

      if (layer.name === layerName) {
        return layer;
      }
    }

    return null;
  }

  public findLayerByTitle(map: any, title: string) {
    if (!map) {
      return null;
    }

    if (!title) {
      return null;
    }

    var layers = map.getLayers().getArray();

    for (let index = 0; index < layers.length; index++) {
      const layer = layers[index];

      if (layer.get('title') === title) {
        return layer;
      }
    }

    return null;
  }

  public getFeaturesExtent(features: any[]) {
    var geoemtries = features.map(function (f) {
      return f.getGeometry();
    });

    var extents = geoemtries.map((x) => x.getExtent());

    console.log(`${this._componentName}.getFeaturesExtent  extents=`, extents);

    if (extents.length === 1) {
      return extents[0];
    } else if (extents.length > 1) {
      return extents[0];
      // TODO: add a method to the spatial service that will combine the extents into one geometry.
      // throw "Multiple features not supported";
    } else {
      throw 'Not Supported';
    }

    // .filter(function (g) { return g instanceof ol.geom.Point; })
    // .map(function (g) { return g.getCoordinates(); });
  }

  /**
   * Transform a lat/lon extent to a web mercator extent
   * @param extent
   * @returns
   */
  public transform(extent: any) {
    return transformExtent(extent, 'EPSG:4326', 'EPSG:3857');
  }
}
